<template>
  <div class="app-container">
    <Header2 />

    <div class="main-content">
      <div class="content-wrapper">
        <!-- Balance Box -->
        <div class="balance-box">
          <div class="balance-header">
            <h3>Account Balance</h3>
          </div>
          <p class="balance-amount">₦{{ balance }}</p>
        </div>

        <!-- Tabs -->
        <div class="tabs-container">
          <div class="tabs-header">
            <button
              @click="
                activeTab = 'transactions';
                fetchTransactions();
              "
              :class="{ active: activeTab === 'transactions' }"
            >
              Transactions
            </button>
            <button
              @click="
                activeTab = 'plans';
                fetchPlans();
              "
              :class="{ active: activeTab === 'plans' }"
            >
              Plans
            </button>
          </div>

          <div class="tab-content">
            <!-- Transactions Tab -->
            <div v-if="activeTab === 'transactions'" class="transactions-tab">
              <div v-if="transactionsLoading" class="loading-spinner">Loading...</div>
              <div v-else>
                <table class="transactions-table">
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Amount</th>
                      <th>Receiver</th>
                      <th>Plan name</th>
                      <th>Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="txn in transactions" :key="txn.id">
                      <td>{{ txn.reference }}</td>
                      <td>₦{{ txn.amount }}</td>
                      <td>{{ txn.receiver }}</td>

                      <td>{{ txn.plan_name }}</td>
                      <td>{{ formatDate(txn.created_at) }}</td>
                      <td :class="'status-' + txn.status">
                        <span class="status-badge">{{ txn.status }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Add this right after the closing </table> tag in your transactions-tab -->
              <div
                v-if="!transactionsLoading && transactions.length"
                class="pagination-container"
              >
                <div class="pagination-info">
                  Showing
                  {{ (pagination.current_page - 1) * pagination.per_page + 1 }}
                  to
                  {{
                    Math.min(
                      pagination.current_page * pagination.per_page,
                      pagination.total
                    )
                  }}
                  of {{ pagination.total }} entries
                </div>
                <div class="pagination-controls">
                  <button
                    @click="fetchTransactions(pagination.current_page - 1)"
                    :disabled="!pagination.links[0].url"
                    class="pagination-btn"
                  >
                    Previous
                  </button>

                  <button
                    v-for="(link, index) in pagination.links.slice(1, -1)"
                    :key="index"
                    @click="fetchTransactions(parseInt(link.label))"
                    :class="{ active: link.active }"
                    class="pagination-btn"
                  >
                    {{ link.label }}
                  </button>

                  <button
                    @click="fetchTransactions(pagination.current_page + 1)"
                    :disabled="!pagination.links[pagination.links.length - 1].url"
                    class="pagination-btn"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>

            <!-- Plans Tab -->
            <div v-if="activeTab === 'plans'" class="plans-tab">
              <div class="plans-header">
                <button @click="openAddModal" class="action-btn">Add Plan</button>
              </div>

              <div v-if="plansLoading" class="loading-spinner">Loading...</div>
              <div v-else class="plans-list">
                <div v-for="plan in plans" :key="plan.id" class="plan-item">
                  <div class="plan-info">
                    <div class="plan-id">{{ plan.pl }}</div>
                    <div class="plan-name">{{ plan.planname }}</div>
                    <div class="plan-meta">
                      <span class="network-badge" :class="'network-' + 1">
                        {{ getNetworkName(1) }}
                      </span>
                      <span class="plan-price">₦{{ plan.price }}</span>
                    </div>
                  </div>
                  <div class="plan-actions">
                    <button @click="openEditModal(plan)" class="edit-btn">Edit</button>
                    <button @click="deletePlan(plan.id)" class="delete-btn">
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Plan Modal -->
    <PlanModal
      :show="showPlanModal"
      :plan="currentPlan"
      :isLoading="isLoading"
      @close="closeModal"
      @save="handleSavePlan"
    />
  </div>
</template>

<script>
import Header2 from "../../components/header.vue";
import PlanModal from "../../components/PlanModal.vue";
import axios from "axios";

export default {
  name: "SettingsApp",
  components: { Header2, PlanModal },
  data() {
    return {
      balance: 0,
      activeTab: "transactions",
      showPlanModal: false,
      currentPlan: null,
      transactions: [],
      plans: [],
      transactionsLoading: false,
      plansLoading: false,
      isLoading: false,
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 10,
        links: [],
        total: 0,
      },
    };
  },
  async created() {
    await this.fetchBalance();
    await this.fetchTransactions();
  },
  methods: {
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    getNetworkName(code) {
      const networks = {
        1: "MTN",
        2: "Airtel",
        3: "Glo",
        4: "9mobile",
      };
      return networks[code] || "Unknown";
    },

    async fetchBalance() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_URL}api/talk/details`,
          {
            headers: {
              Authorization: "Bearer " + JSON.parse(localStorage.getItem("admin")).token,
            },
          }
        );

        console.log("Balance response:", response);
        this.balance = response.data.data.data.balance || 0;
      } catch (error) {
        console.error("Error fetching balance:", error);
      }
    },

    async fetchTransactions(page = 1) {
      this.transactionsLoading = true;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_URL}api/talk/transaction?page=${page}`,
          {
            headers: {
              Authorization: "Bearer " + JSON.parse(localStorage.getItem("admin")).token,
            },
          }
        );

        this.transactions = response.data.data.transactions.data || [];
        this.pagination = {
          current_page: response.data.data.transactions.current_page || 1,
          last_page: response.data.data.transactions.last_page || 1,
          per_page: response.data.data.transactions.per_page || 10,
          links: response.data.data.transactions.links || [],
          total: response.data.data.transactions.total || 0,
        };
      } catch (error) {
        console.error("Error fetching transactions:", error);
      } finally {
        this.transactionsLoading = false;
      }
    },
    async fetchPlans() {
      this.plansLoading = true;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_URL}api/talk/plans`,
          {
            headers: {
              Authorization: "Bearer " + JSON.parse(localStorage.getItem("admin")).token,
            },
          }
        );
        this.plans = response.data.data || [];

        console.log("Plans response:", response);
      } catch (error) {
        console.error("Error fetching plans:", error);
      } finally {
        this.plansLoading = false;
      }
    },

    openEditModal(plan) {
      this.currentPlan = { ...plan };
      this.showPlanModal = true;
    },

    openAddModal() {
      this.currentPlan = {};
      this.showPlanModal = true;
    },

    closeModal() {
      this.showPlanModal = false;
      this.currentPlan = null;
    },

    async handleSavePlan(planData) {
      try {
        this.isLoading = true;
        const url = planData.id
          ? `${process.env.VUE_APP_BASE_URL}api/talk/update-price`
          : `${process.env.VUE_APP_BASE_URL}api/talk/add-plan`;

        const response = await axios.post(url, planData, {
          headers: {
            Authorization: "Bearer " + JSON.parse(localStorage.getItem("admin")).token,
          },
        });

        if (planData.id) {
          this.plans = this.plans.map((p) =>
            p.id === planData.id ? response.data.plan : p
          );
        } else {
          this.plans.push(response.data.plan);
        }
        this.fetchPlans();
        this.showPlanModal = false;

        this.closeModal();
      } catch (error) {
        console.error("Error saving plan:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async deletePlan(planId) {
      if (!confirm("Are you sure you want to delete this plan?")) return;

      try {
        await axios.post(
          `${process.env.VUE_APP_BASE_URL}api/talk/delete-plan`,
          { id: planId },
          {
            headers: {
              Authorization: "Bearer " + JSON.parse(localStorage.getItem("admin")).token,
            },
          }
        );
        this.plans = this.plans.filter((plan) => plan.id !== planId);
      } catch (error) {
        console.error("Error deleting plan:", error);
      }
    },
  },
};
</script>

<style scoped>
.app-container {
  display: flex;
  min-height: 100vh;
}

.main-content {
  flex: 1;
  padding: 20px;
  margin-left: 300px; /* Adjust based on your sidebar width */
}

.content-wrapper {
  max-width: 1200px;
  margin: 0 auto;
}

/* Balance Box */
.balance-box {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  margin-top: 60px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.balance-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.balance-header h3 {
  font-size: 14px;
  font-weight: 500;
  color: #555;
  margin: 0;
}

.balance-amount {
  font-size: 24px;
  font-weight: 600;
  color: #0a1aa8;
  margin: 0;
}

.refresh-btn {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
}

/* Tabs */
.tabs-container {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.tabs-header {
  display: flex;
  border-bottom: 1px solid #eee;
}

.tabs-header button {
  padding: 12px 20px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #666;
  position: relative;
}

.tabs-header button.active {
  color: #0a1aa8;
  font-weight: 600;
}

.tabs-header button.active::after {
  content: "";
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 100%;
  height: 2px;
  background: #0a1aa8;
}

.tab-content {
  padding: 16px;
}

/* Transactions Table */
.transactions-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.transactions-table th,
.transactions-table td {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.transactions-table th {
  font-weight: 600;
  color: #444;
}

.transactions-table tr:hover {
  background-color: #f9f9f9;
}

.status-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-success .status-badge {
  background-color: #e6f7ee;
  color: #21b55a;
}

.status-pending .status-badge {
  background-color: #fff8e6;
  color: #ffa500;
}

.status-failed .status-badge {
  background-color: #ffebeb;
  color: #ff0000;
}

/* Plans Tab */
.plans-header {
  margin-bottom: 16px;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #0a1aa8;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
}

.plans-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
}

.plan-item {
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.plan-info {
  flex: 1;
}

.plan-id {
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.plan-name {
  font-size: 12px;
  color: #666;
  margin-bottom: 8px;
}

.plan-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.network-badge {
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 10px;
  font-weight: 500;
}

.network-1 {
  background: #f0f7ff;
  color: #0066cc;
}
.network-2 {
  background: #fff0f0;
  color: #cc0000;
}
.network-3 {
  background: #f0fff0;
  color: #008000;
}
.network-4 {
  background: #fff8f0;
  color: #ff6600;
}

.plan-price {
  font-size: 13px;
  font-weight: 600;
  color: #333;
}

.plan-actions {
  display: flex;
  gap: 6px;
}

.edit-btn,
.delete-btn {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
}

.edit-btn {
  background: #e6f7ee;
  color: #21b55a;
}

.delete-btn {
  background: #ffebeb;
  color: #ff0000;
}

/* Loading Spinner */
.loading-spinner {
  display: flex;
  justify-content: center;
  padding: 40px;
  color: #0a1aa8;
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding: 16px;
  }

  .plans-list {
    grid-template-columns: 1fr;
  }
}

/* Pagination Styles */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding: 10px 0;
  border-top: 1px solid #eee;
}

.pagination-info {
  font-size: 13px;
  color: #666;
}

.pagination-controls {
  display: flex;
  gap: 5px;
}

.pagination-btn {
  padding: 6px 12px;
  border: 1px solid #ddd;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  background: #f5f5f5;
}

.pagination-btn:disabled {
  color: #ccc;
  cursor: not-allowed;
}

.pagination-btn.active {
  background: #0a1aa8;
  color: white;
  border-color: #0a1aa8;
}
</style>
